import { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';

export const useCreditTransactions = (userId) => {
    const [transactions, setTransactions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [transactionType, setTransactionType] = useState('all');

    useEffect(() => {
        if (userId) {
            fetchTransactions();
        }
    }, [userId]);

    const fetchTransactions = async () => {
        try {
            setLoading(true);

            let query = supabase
                .from('credit_transactions')
                .select('*')
                .eq('user_id', userId)
                .order('created_at', { ascending: false });

            const { data, error } = await query;

            if (error) throw error;

            setTransactions(data);
            setError(null);
        } catch (err) {
            console.error('Error fetching transactions:', err);
            setError('Failed to load transactions. Please try again later.');
        } finally {
            setLoading(false);
        }
    };

    const formatTransactionId = (id) => `TR-${id.substring(0, 8).toUpperCase()}`;
    const formatReferenceId = (id) => `REF-${id.substring(0, 8).toUpperCase()}`;
    const formatStripeId = (id) => `STRP-${id.substring(0, 8).toUpperCase()}`;

    const getTransactionTypeLabel = (type) => {
        const labels = {
            'analysis': 'Website Analysis',
            'analysis_refund': 'Analysis Refund',
            'credit_purchase': 'Credit Purchase',
            'custom_type_creation': 'Custom Type Creation'
        };
        return labels[type] || type;
    };

    const normalizeSearchTerm = (term) => {
        const normalized = term.toLowerCase();
        // Remove prefixes if they exist
        if (normalized.startsWith('tr-')) return normalized.substring(3);
        if (normalized.startsWith('ref-')) return normalized.substring(4);
        if (normalized.startsWith('strp-')) return normalized.substring(5);
        return normalized;
    };

    const matchesId = (searchNormalized, id) => {
        if (!id) return false;
        const idNormalized = id.substring(0, 8).toLowerCase();
        return idNormalized.includes(searchNormalized);
    };

    const filteredTransactions = transactions.filter(transaction => {
        if (!searchTerm) return true;

        const searchNormalized = normalizeSearchTerm(searchTerm);

        // Check if searching for credits amount
        if (!isNaN(searchTerm) && transaction.amount.toString().includes(searchTerm)) {
            return true;
        }

        // Format IDs the same way they're displayed in the table
        const transactionIdFormatted = formatTransactionId(transaction.id).toLowerCase();
        const referenceIdFormatted = transaction.reference_id ?
            formatReferenceId(transaction.reference_id).toLowerCase() : '';
        const stripeIdFormatted = transaction.stripe_payment_id ?
            formatStripeId(transaction.stripe_payment_id).toLowerCase() : '';

        // Search in formatted IDs
        return (
            transactionIdFormatted.includes(searchTerm.toLowerCase()) ||
            (referenceIdFormatted && referenceIdFormatted.includes(searchTerm.toLowerCase())) ||
            (stripeIdFormatted && stripeIdFormatted.includes(searchTerm.toLowerCase())) ||
            // Also search in raw IDs with normalized search term
            matchesId(searchNormalized, transaction.id) ||
            matchesId(searchNormalized, transaction.reference_id) ||
            matchesId(searchNormalized, transaction.stripe_payment_id)
        );
    }).filter(transaction => {
        return transactionType === 'all' || transaction.transaction_type === transactionType;
    });

    return {
        transactions: filteredTransactions,
        loading,
        error,
        searchTerm,
        setSearchTerm,
        transactionType,
        setTransactionType,
        getTransactionTypeLabel,
        formatTransactionId,
        formatReferenceId,
        formatStripeId,
        refresh: fetchTransactions
    };
};

export default useCreditTransactions;